import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { logAnalyticsEvent } from '@/lib/firebase';
import { useCreateContactMessage } from '@/hooks/use-content';
import SectionEyebrow from './SectionEyebrow';

const CONTACT_EMAIL = 'ahmadyousafzai30@gmail.com';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);
  // Off-screen field a human never sees or fills; naive form-spam bots fill
  // every input they find. If it has a value, the "send" is silently dropped.
  const [honeypot, setHoneypot] = useState('');
  const createMessage = useCreateContactMessage();
  const loading = createMessage.isPending;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFailed(false);

    const message = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
    };

    try {
      // Bots get the same success screen so they learn nothing, but nothing is stored.
      if (!honeypot) await createMessage.mutateAsync(message);
      logAnalyticsEvent('contact_form_submit');
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (error) {
      // Never show "sent" for a message that wasn't stored — the visitor would
      // wait for a reply that can't come. Show the failure and a direct way out.
      console.error('Error sending message:', error);
      setFailed(true);
    }
  };

  return (
    <section id="contact" className="py-14 sm:py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <SectionEyebrow index="10" label={t('contact.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('contact.title')}</h2>
          <p className="text-muted-foreground text-lg">{t('contact.description')}</p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-card p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  {t('contact.nameLabel')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength={200}
                  className="w-full px-4 py-2 rounded-lg border border-input bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t('contact.namePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  {t('contact.emailLabel')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  maxLength={320}
                  className="w-full px-4 py-2 rounded-lg border border-input bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-2">
                  {t('contact.messageLabel')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={5000}
                  rows={5}
                  className="w-full px-4 py-2 rounded-lg border border-input bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder={t('contact.messagePlaceholder')}
                />
              </div>

              {/* Honeypot — see the state above. Not display:none, which some bots skip. */}
              <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              {submitted && (
                <div role="status" className="p-4 bg-green-500/20 text-green-700 rounded-lg text-center">
                  {t('contact.successMessage')}
                </div>
              )}

              {failed && (
                <div role="alert" className="p-4 bg-destructive/15 text-destructive rounded-lg text-center">
                  {t('contact.errorMessage')}{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-medium">
                    {CONTACT_EMAIL}
                  </a>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-primary hover:scale-[1.02] transition-all"
              >
                {loading ? t('contact.sending') : t('contact.send')}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
